/**
 * Points the browser-tab icon at the client's own logo.
 *
 * A candidate arrives from an email link and may leave the tab open for the length of a recording session. The icon they see there should be the company they are applying to, not our stand-in \"A\".
 *
 * Three things make this less trivial than setting an attribute:
 *
 *  1. **`<link rel="icon">` fires no `load` or `error` event.** A tab icon that 404s just leaves
 *     the browser showing a blank page-shaped placeholder, with nothing to react to. So the bytes
 *     are fetched into an `Image` first and the tag is only repointed once they have decoded.
 *  2. **The `type` attribute has to go.** The built-in icon is declared `image/svg+xml`, and a
 *     browser handed PNG bytes under that type is entitled to render nothing at all. A logo may be
 *     any of four formats, so the declaration is dropped and the bytes are left to speak.
 *  3. **A superseded upload must not win.** Two probes can be in flight after a quick re-upload,
 *     and the slower one finishing last would put the old logo back. A token settles the order.
 *
 * If anything fails the built-in favicon stays exactly where it was.
 *
 * This is deliberately duplicated in the other app rather than shared: `@altamont/shared` also
 * runs inside Workers, where there is no `document` to touch.
 */
import { onBeforeUnmount, watch } from 'vue';

/** Finds the tab-icon tag written into `index.html`. */
function iconLink(): HTMLLinkElement | null {
  if (typeof document === 'undefined') return null;
  return document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
}

export function useBrandFavicon(source: () => string | null): void {
  /** The built-in icon, remembered before the first swap so it can always be put back. */
  let original: string | null = null;
  /** Identifies the most recent request, so a slow earlier probe cannot overwrite a newer one. */
  let token = 0;

  function restore(link: HTMLLinkElement): void {
    if (original !== null) link.setAttribute('href', original);
  }

  function apply(href: string | null): void {
    const link = iconLink();
    if (link === null) return;
    if (original === null) original = link.getAttribute('href');

    token += 1;
    const mine = token;

    if (href === null || href === '') {
      restore(link);
      return;
    }

    const probe = new Image();
    probe.onload = (): void => {
      if (mine !== token) return;
      link.setAttribute('href', href);
      // See (2) above: the built-in icon is an SVG and a client's logo usually is not.
      link.removeAttribute('type');
    };
    probe.onerror = (): void => {
      if (mine === token) restore(link);
    };
    probe.src = href;
  }

  watch(source, apply, { immediate: true });

  onBeforeUnmount(() => {
    token += 1;
    const link = iconLink();
    if (link !== null) restore(link);
  });
}
